import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { db, collection, getDocs, query, where, Timestamp } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { FiPlus, FiCalendar, FiMap, FiDollarSign, FiFilter, FiSearch, FiClock, FiArrowRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { EventCard } from "../components/EventCard";
import { getMinTicketPrice, getTicketTiersArray } from '../utils/eventHelpers';
import EventSkeleton, { EventSkeletonGroup } from '../components/EventSkeleton';

interface TicketTier {
  id: string;
  name: string;
  price: number;
  quantity: number;
  description: string;
  sold: number;
}

interface Event {
  id: string;
  title: string;
  description: string;
  startDateTime: Timestamp;
  endDateTime: Timestamp;
  location: {
    address: string;
    city?: string;
    state?: string;
    zipCode?: string;
    lat?: number;
    lng?: number;
  };
  category: string;
  ticketTiers: TicketTier[];
  organizerId: string;
  organizerName: string;
  imageUrl?: string;
  coverImage?: string;
  media?: {
    images?: string[];
    video?: string;
  };
  createdAt: Timestamp;
  status: 'draft' | 'published' | 'cancelled' | 'completed';
  updatedAt: Timestamp;
}

interface FilterOptions {
  category: string;
  priceRange: string;
  date: string;
}

// Fix the ErrorFallback component with proper types
const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  return (
    <Card className="text-center p-6">
      <CardContent className="pt-6">
        <div className="flex flex-col items-center justify-center py-8">
          <div className="rounded-full bg-red-100 p-6 mb-4">
            <FiCalendar className="h-8 w-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">Something went wrong</h3>
          <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
            {error.message || "There was an error loading events"}
          </p>
          <Button
            onClick={resetErrorBoundary}
            className="mt-6"
          >
            Try Again
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function Events() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState<FilterOptions>({
    category: 'all',
    priceRange: 'all',
    date: 'all'
  });
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const { category } = useParams();

  const categories = ['All', 'Music', 'Technology', 'Business', 'Arts', 'Sports', 'Food', 'Education'];
  const priceRanges = ['All', 'Free', 'Under $25', '$25-$50', '$50-$100', '$100+'];
  const dateFilters = ['All', 'Today', 'This Week', 'This Month', 'Future'];

  useEffect(() => {
    if (category) {
      setFilters(f => ({ ...f, category: category.toLowerCase() }));
    }
  }, [category]);

  // Use useCallback to prevent fetch function from being recreated on every render
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching events from Firestore...');
      const eventsRef = collection(db, 'events');
      
      // Only fetch published events
      let eventsQuery = query(eventsRef);

      console.log('Executing Firestore query:', eventsQuery);
      const querySnapshot = await getDocs(eventsQuery);
      
      if (querySnapshot.empty) {
        console.log('No events found in Firestore');
        setEvents([]);
        setLoading(false);
        return;
      }
      
      console.log(`Found ${querySnapshot.docs.length} events in Firestore`);
      
      // Map Firestore documents to Event objects with careful type handling
      const eventsData: Event[] = [];
      
      querySnapshot.docs.forEach(doc => {
        try {
          const data = doc.data();
          
          // Handle missing or malformed data with defaults
          const event: Event = {
            id: doc.id,
            title: data.title || 'Untitled Event',
            description: data.description || 'No description available',
            // Handle different date field names and formats
            startDateTime: data.startDateTime || data.startDate || data.date || Timestamp.now(),
            endDateTime: data.endDateTime || data.endDate || data.startDateTime || Timestamp.now(),
            location: {
              address: data.address || (data.location && data.location.address) || 'No address specified',
              city: data.city || (data.location && data.location.city),
              state: data.state || (data.location && data.location.state),
              zipCode: data.zipCode || (data.location && data.location.zipCode),
            },
            category: data.category || 'Uncategorized',
            ticketTiers: Array.isArray(data.ticketTiers) ? data.ticketTiers : [],
            organizerId: data.organizerId || 'unknown',
            organizerName: data.organizerName || 'Anonymous Organizer',
            imageUrl: data.imageUrl || null,
            coverImage: data.coverImage || null,
            media: data.media || { images: [] },
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            status: (data.status as 'draft' | 'published' | 'cancelled' | 'completed') || 'published'
          };
          
          eventsData.push(event);
        } catch (err) {
          console.error('Error processing event document:', err);
          // Skip this event instead of adding a placeholder that might cause issues
        }
      });
      
      console.log('Processed events data:', eventsData);
      
      // Sort events by date safely
      try {
        eventsData.sort((a, b) => {
          const dateA = a.startDateTime instanceof Timestamp ? 
            a.startDateTime.toMillis() : 
            new Date(a.startDateTime).getTime();
            
          const dateB = b.startDateTime instanceof Timestamp ? 
            b.startDateTime.toMillis() : 
            new Date(b.startDateTime).getTime();
            
          return dateA - dateB;
        });
      } catch (error) {
        console.error('Error sorting events:', error);
        // Continue without sorting rather than failing the entire operation
      }

      setEvents(eventsData);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events. Please try again later.');
      // Don't show toast on every render as it might cause infinite loops
    } finally {
      setLoading(false);
    }
  }, []); // Keep empty dependency array since this function doesn't use component state

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleCreateEvent = () => {
    if (user) {
      navigate('/events/create');
    } else {
      toast.error('You must be logged in to create an event');
      navigate('/login');
    }
  };

  const getMinPrice = (ticketTiers: any): number => {
    return getMinTicketPrice({ ticketTiers }) || 0;
  };

  const formatEventDate = (date: Timestamp | any) => {
    if (!date) return 'Date not set';
    
    try {
      if (date instanceof Timestamp) {
        return format(date.toDate(), 'EEE, MMM d, yyyy • h:mm a');
      } else if (typeof date === 'string') {
        return format(new Date(date), 'EEE, MMM d, yyyy • h:mm a');
      } else if (date.toDate && typeof date.toDate === 'function') {
        return format(date.toDate(), 'EEE, MMM d, yyyy • h:mm a');
      }
      return format(new Date(date), 'EEE, MMM d, yyyy • h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Invalid date';
    }
  };

  const getEventStatus = (event: Event): string => {
    try {
      if (!event.startDateTime) return 'unknown';
      
      const startDate = event.startDateTime instanceof Timestamp 
        ? event.startDateTime.toDate() 
        : new Date(event.startDateTime);
        
      const endDate = event.endDateTime instanceof Timestamp 
        ? event.endDateTime.toDate() 
        : event.startDateTime instanceof Timestamp 
          ? event.startDateTime.toDate() 
          : new Date(event.startDateTime);
      
      const now = new Date();
      
      if (now > endDate) return 'completed';
      if (now >= startDate && now <= endDate) return 'ongoing';
      return 'upcoming';
    } catch (error) {
      console.error('Error determining event status:', error);
      return 'unknown';
    }
  };

  // Safely filter events to prevent crashes
  const safeFilterEvents = (events: Event[]): Event[] => {
    try {
      // First filter by tab selection
      let filtered = events || [];
      
      if (activeTab !== 'all') {
        filtered = filtered.filter(event => {
          try {
            return getEventStatus(event) === activeTab;
          } catch {
            return false;
          }
        });
      }
      
      // Then apply the rest of the filters
      filtered = filtered.filter(event => {
        try {
          // Search term filter
          if (searchTerm && event?.title && event?.description) {
            if (!event.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
                !event.description.toLowerCase().includes(searchTerm.toLowerCase())) {
              return false;
            }
          }

          // Category filter
          if (filters.category !== 'all' && event?.category) {
            if (event.category.toLowerCase() !== filters.category) {
              return false;
            }
          }

          // Price range filter
          if (event?.ticketTiers) {
            const lowestPrice = getMinPrice(event.ticketTiers);
            if (filters.priceRange !== 'all') {
              if (filters.priceRange === 'free' && lowestPrice !== 0) return false;
              if (filters.priceRange === 'under $25' && (lowestPrice === 0 || lowestPrice >= 25)) return false;
              if (filters.priceRange === '$25-$50' && (lowestPrice < 25 || lowestPrice > 50)) return false;
              if (filters.priceRange === '$50-$100' && (lowestPrice < 50 || lowestPrice > 100)) return false;
              if (filters.priceRange === '$100+' && lowestPrice < 100) return false;
            }
          }

          // Skip date filter if we don't have a valid startDateTime
          if (!event?.startDateTime) {
            return true;
          }
          
          try {
            const eventDate = event.startDateTime instanceof Timestamp 
              ? event.startDateTime.toDate() 
              : new Date(event.startDateTime);
            
            const now = new Date();
            
            if (filters.date !== 'all') {
              if (filters.date === 'today' && format(eventDate, 'yyyy-MM-dd') !== format(now, 'yyyy-MM-dd')) return false;
              if (filters.date === 'this week' && eventDate > new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)) return false;
              if (filters.date === 'this month' && eventDate.getMonth() !== now.getMonth()) return false;
              if (filters.date === 'future' && eventDate < now) return false;
            }
          } catch (error) {
            console.error('Error filtering by date:', error);
            // Continue with other filters instead of failing
          }

          return true;
        } catch (err) {
          console.error('Error filtering event:', err);
          return false;
        }
      });
      
      return filtered;
    } catch (err) {
      console.error('Error in filterEvents function:', err);
      return events || []; // Return original events instead of empty array
    }
  };

  // Safely apply filters with type annotation
  let filteredEvents: Event[] = [];
  try {
    // Only show published events to the public
    filteredEvents = safeFilterEvents(events.filter(event => event.status === 'published'));
  } catch (err) {
    console.error('Error filtering events:', err);
    filteredEvents = events.filter(event => event.status === 'published'); // Use only published events if filtering fails
  }

  // Handle errors in render safely
  const renderContent = () => {
    try {
      if (loading) {
        return (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, index) => (
              <EventSkeleton key={index} />
            ))}
          </div>
        );
      }
      
      if (filteredEvents.length === 0) {
        return (
          <Card className="text-center p-10">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center py-12">
                <div className="rounded-full bg-gray-100 p-6 mb-4">
                  <FiCalendar className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">No events found</h3>
                <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
                  {events.length === 0 ? 'Be the first to create an event!' : 'Try adjusting your filters'}
                </p>
                {(hasRole('organizer') || hasRole('admin')) && events.length === 0 && (
                  <Button
                    onClick={handleCreateEvent}
                    className="mt-6"
                  >
                    <FiPlus className="mr-2 h-4 w-4" />
                    Create Event
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      }
      
      return (
        <div className="w-full max-w-full px-2 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-8 mb-6">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
            
            <Select
              value={filters.category}
              onValueChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(category => (
                  <SelectItem key={category} value={category.toLowerCase()}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.priceRange}
              onValueChange={(value) => setFilters(prev => ({ ...prev, priceRange: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Price Range" />
              </SelectTrigger>
              <SelectContent>
                {priceRanges.map(range => (
                  <SelectItem key={range} value={range.toLowerCase()}>
                    {range}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="wait">
              {filteredEvents.map((event, idx) => {
                if (!event || !event.id) return null;
                
                try {
                  return (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ 
                        duration: 0.3,
                        type: "spring",
                        stiffness: 100,
                        damping: 15
                      }}
                      layout
                    >
                      <ErrorBoundary 
                        FallbackComponent={() => (
                          <Card className="h-full flex flex-col">
                            <CardContent className="flex-1 flex items-center justify-center text-center p-6">
                              <div>
                                <p className="text-red-500 mb-2">Error loading event</p>
                                <p className="text-sm text-gray-500">{event?.title || 'Unknown Event'}</p>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      >
                        <EventCard event={event} index={idx} />
                      </ErrorBoundary>
                    </motion.div>
                  );
                } catch (err) {
                  console.error('Error rendering event card:', err);
                  return null;
                }
              })}
            </AnimatePresence>
          </div>
        </div>
      );
    } catch (err) {
      console.error('Error in renderContent:', err);
      return (
        <Card className="text-center p-10">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12">
              <h3 className="text-lg font-medium text-gray-900">Error loading events</h3>
              <p className="mt-1 text-sm text-gray-500">
                There was a problem loading the events.
              </p>
              <Button onClick={() => window.location.reload()} className="mt-6">
                Reload Page
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={fetchEvents}
    >
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Discover Events</h1>
              <p className="text-gray-500 mt-1">Find and book your next amazing experience</p>
            </div>
            
            {(hasRole('organizer') || hasRole('admin')) && (
              <Button 
                onClick={handleCreateEvent}
                size="lg"
                className="group"
              >
                <FiPlus className="mr-2 h-5 w-5" />
                Create Event
                <FiArrowRight className="ml-2 h-4 w-0 group-hover:w-4 transition-all overflow-hidden" />
              </Button>
            )}
          </div>
          
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="grid w-full md:w-auto grid-cols-4">
              <TabsTrigger value="all">All Events</TabsTrigger>
              <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
              <TabsTrigger value="ongoing">Ongoing</TabsTrigger>
              <TabsTrigger value="completed">Past</TabsTrigger>
            </TabsList>
          </Tabs>

          {renderContent()}
        </div>
      </div>
    </ErrorBoundary>
  );
} 